/**
 * Alpine.js wrapper component for creating a single product hero section.
 * Features include:
 * - Initializing the product carousel with Swiper.
 * - Setting the slide by image URL.
 * - Separating standalone variant if present.
 * - Fetching product data and bundle information.
 * - Updating product data and selected options.
 * - Handling option changes and updating the current variant accordingly.
 * - param productHandle - The handle of the product to display.
 * - Returns Object containing methods and properties for managing the single product hero.
 * Note: This component should be combined with bundleProductHero in the future.
 */

function singleProductHero(productHandle) {
  return {
    error: false,
    loading: true,
    product: null,
    currentVariant: null,
    selectedOptions: {},
    mainSwiper: null,
thumbsSwiper: null,

    
initCarousel() {
  this.$nextTick(() => {
    if (typeof Swiper === 'undefined') {
      console.error('Swiper library not loaded');
      return;
    }

    const root = this.$el;

    const mainElement = root.querySelector(
      '.swiper-container-pdp-gallery'
    );

    const thumbnailsElement = root.querySelector(
      '.thumbnails-swiper'
    );

    if (!mainElement) {
      console.error('Main gallery not found');
      return;
    }

    // Destroy previous instances
    if (this.mainSwiper && !this.mainSwiper.destroyed) {
      this.mainSwiper.destroy(true, true);
    }

    if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
      this.thumbsSwiper.destroy(true, true);
    }

    this.mainSwiper = null;
    this.thumbsSwiper = null;

    // Initialize thumbnail slider
    if (thumbnailsElement) {
      this.thumbsSwiper = new Swiper(thumbnailsElement, {
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        allowTouchMove: true,
        simulateTouch: true,
      });
    }

    const pagination = root.querySelector(
      '.swiper-pagination-pdp-gallery'
    );

    const nextButton = root.querySelector(
      '.swiper-pdp-gallery-next'
    );

    const prevButton = root.querySelector(
      '.swiper-pdp-gallery-prev'
    );

    // Initialize main gallery
    this.mainSwiper = new Swiper(mainElement, {
      slidesPerView: 'auto',
      rewind: true,
      speed: 300,

      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: true,

      touchEventsTarget: 'container',
      touchStartPreventDefault: false,
      threshold: 5,
      touchAngle: 45,
      passiveListeners: false,

      observer: true,
      observeParents: true,
      resizeObserver: true,

      pagination: pagination
        ? {
            el: pagination,
            type: 'progressbar',
          }
        : undefined,

      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      },

      thumbs: this.thumbsSwiper
        ? {
            swiper: this.thumbsSwiper,
          }
        : undefined,
    });

    // Recalculate after Safari layout
    requestAnimationFrame(() => {
      if (
        this.mainSwiper &&
        !this.mainSwiper.destroyed
      ) {
        this.mainSwiper.update();
      }

      if (
        this.thumbsSwiper &&
        !this.thumbsSwiper.destroyed
      ) {
        this.thumbsSwiper.update();
      }

      const pdpGallery = root.querySelector(
        '.pdp-product-gallery'
      );

      const pdpThumbnails = root.querySelector(
        '.pdp-product-gallery-thumbnails'
      );

      if (
        pdpGallery &&
        pdpThumbnails &&
        this.thumbsSwiper
      ) {
        const isSwipeable =
          this.thumbsSwiper.virtualSize >
          pdpGallery.offsetWidth;

        pdpGallery.classList.toggle(
          'centered-thumbnails',
          !isSwipeable
        );

        pdpThumbnails.classList.toggle(
          'max-content',
          !isSwipeable
        );
      }
    });

    console.log('Main Swiper initialized:', this.mainSwiper);
    console.log('Thumbnail Swiper initialized:', this.thumbsSwiper);

    this.$dispatch('swipers-initialized');
  });
},


    setSlideByImageUrl(imageUrl) {
      if (!this.mainSwiper) {
        console.error('Swiper not initialized');
        return;
      }

      const slideIndex = this.mainSwiper.slides.findIndex((slide) => {
        const img = slide.querySelector('img');
        return img && img.src === imageUrl;
      });

      if (slideIndex !== -1) {
        this.mainSwiper.slideTo(slideIndex, 0);
      } else {
        console.warn('No slide found with the specified image URL');
      }
    },

    get pageTitle() {
      return this.product.name;
    },

    separateStandaloneVariant() {
      if (!this.product || !this.product.variants) {
        return;
      }
      const index = this.product.variants.findIndex((variant) => variant.standaloneVariant === true);
      if (index !== -1) {
        this.product.standaloneVariant = this.product.variants.splice(index, 1)[0];
      } else {
        this.product.standaloneVariant = null;
      }
    },

    get shopifyData() {
      return this.product;
    },

    get currentProduct() {
      return this.product;
    },

    async initialSetup() {
      try {
        const productData = await fetchProductData(productHandle);
        if (productData.bundleHandle) {
          const bundleData = await fetchBundle(productData.bundleHandle);
          productData.bundleProduct = bundleData;
        }
        this.product = productData;
        this.separateStandaloneVariant();
        this.initializeSelectedOptions();
        this.initCarousel();
      } catch (error) {
        console.error('Error fetching product data:', error);
        this.error = true;
      }
      this.loading = false;
    },

    updateProduct(handle) {
      if (handle === this.product.handle) {
        this.initializeSelectedOptions();
        this.$dispatch('current-product-change', {
          product: this.product,
        });
        this.initCarousel();
      } else {
        console.error('Product data not found for handle:', handle);
      }
    },

    get optionValues() {
      if (!this.product || !this.product.variants) {
        return {};
      }
      const optionValues = {};
      this.product.variants.forEach((variant) => {
        variant.options?.forEach((option) => {
          if (!optionValues[option.name]) {
            optionValues[option.name] = new Set();
          }
          optionValues[option.name].add(option.value);
        });
      });
      for (const key in optionValues) {
        optionValues[key] = Array.from(optionValues[key]);
      }

      return optionValues;
    },

    get currentName() {
      return this.product && this.product.name.endsWith('Hammock')
        ? this.product.name.slice(0, -7).trim()
        : this.product.name;
    },

    get hammockSwatches() {
      return null;
    },

    get variantSwatches() {
      let availableVariants = Object.values(this.product.variants);
      availableVariants = availableVariants.filter(product => {
          return product.availableForSale && product.disableSwatch !== 'true'
      })
      return availableVariants.map((variant) => ({
        id: variant.id,
        swatchImage: variant.variantProperties.swatch_image_reference,
        badgeText: variant.variantProperties.swatch_badge_text,
        badgeBg: variant.variantProperties.badge_color,
        badgeTextColor: variant.variantProperties.badge_text_color,
        varImage: variant.image,
      }));
    },

    get badges() {
      return this.product ? getProductBadges(this.product) : [];
    },

    initializeSelectedOptions() {
      if (!this.product || !this.product.variants || this.product.variants.length === 0) {
        return;
      }
      const optionCategory = this.product.options[0].name;
      this.selectedOptions[optionCategory] = this.product.variants[0].selectedOptions[0].value;
      this.currentVariant = this.product.variants[0];
    },

    handleOptionChange(optionName, optionValue) {
      this.selectedOptions[optionName] = optionValue;
      this.updateCurrentVariant();
    },

    updateCurrentVariant: function () {
      const optionCategory = this.product.options[0].name;
      const selectedValue = this.selectedOptions[optionCategory];
      this.currentVariant = this.product.variants.find((variant) =>
        variant.options.some((option) => option.name === optionCategory && option.value === selectedValue),
      );
    },

    init: async function () {
      await this.initialSetup();
      this.loading = false;
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('singleProductHero', singleProductHero);
});
